/**
 * 折り名刺（二つ折り・三つ折り・四つ折り、各変形サイズ）の注文（折りタイプの選択、価格表の表示とカート追加）
 * MEMO: 旧 js/order-meishi-ori.js の ESM 版
 *       用紙・オプション・加工の選択 UI は、src/order-common が #j-nxg_* の hidden input に値を入れる
 *       PP 加工の ID の折りの向きへの付け替えは、utils/order-options.ts の getPpOptionId() が価格・カートの両方で行う
 */
import '@/order-common'
import { reloadPrice, setPriceLoader } from '@/order-common/price-loader'
import { onReady, queryAll } from '@/utils/dom'
import { createPriceTableLoader, initPriceLinkAddCart } from '@/utils/order-price-table'
import { renderSelectedProcessing, setProcessingDisabledBy } from '@/order-common/processing'
import { refreshPriceTable, showOrderLoader } from '@/utils/price-table'

//印刷タイプ（両面白黒・カラー×白黒・両面カラー）
const PRINT_TYPES = ['OPT_PRINT_TYPE_1_1', 'OPT_PRINT_TYPE_4_1', 'OPT_PRINT_TYPE_4_4']

//ページごとの折りタイプの初期値
const INITIAL_ORI_TYPES: Record<string, string> = {
    'order-meishi-2ori': 'OPT_OTHER_MEISHI_ORI_TYPE_2ORI_TATE',
    'order-meishi-2ori-henkei': 'OPT_OTHER_MEISHI_ORI_TYPE_2ORI_TATE',
    'order-meishi-3ori': 'OPT_OTHER_MEISHI_ORI_TYPE_3ORI_TATE_MAKI',
    'order-meishi-3ori-henkei': 'OPT_OTHER_MEISHI_ORI_TYPE_3ORI_TATE_MAKI',
    'order-meishi-4ori': 'OPT_OTHER_MEISHI_ORI_TYPE_4ORI_TATE_KANNON',
    'order-meishi-4ori-henkei': 'OPT_OTHER_MEISHI_ORI_TYPE_4ORI_TATE_KANNON',
}
//横型の折りでは角丸加工ができないページ（三つ折り・四つ折り）
const KADOMARU_DISABLE_PAGES = ['order-meishi-3ori', 'order-meishi-3ori-henkei', 'order-meishi-4ori', 'order-meishi-4ori-henkei']
//角丸加工の選択不可の理由（横型の折り）
const KADOMARU_DISABLED_REASON = 'ori-yoko'
//折りの向きで価格が変わる加工（選択されていれば、折りタイプの変更時に価格表をリセット）
const ORI_DEPENDENT_PROCESSING = ['#order-proc_check-pp_gross', '#order-proc_check-pp_matte']

const pageId = document.body.id
const getOriTypeInput = (): HTMLInputElement | null => document.querySelector<HTMLInputElement>('#j-nxg_ori_type_opt')
const getKadomaruCheck = (): HTMLInputElement | null => document.querySelector<HTMLInputElement>('#order-proc_check-kdmr')

/**
 * 角丸箇所指定の向きを選択（縦型の折りは縦向き、横型の折りは横向き）
 *   MEMO: order-common の向きの切替え処理（画像の切替えなど）を動かすため、ラジオボタンのクリックで選択する
 */
const selectKadomaruOrientation = (isTate: boolean): void => {
    document.querySelector<HTMLInputElement>(isTate ? '#order-proc_kdmr_type-portrait' : '#order-proc_kdmr_type-landscape')?.click()
}

/**
 * 横型の三つ折り・四つ折りで、選択済みの角丸加工を外して選択不可にする
 */
const disableKadomaru = (): void => {
    if(getKadomaruCheck()?.checked) {
      //未選択に戻すためにクリック
        getKadomaruCheck()?.click()
      /*
      * 選択済みを表示する要素から角丸加工を削除する制御を個別実装
      * MEMO: 現在の仕様上、選択済みを表示する要素の削除のトリガーが「追加する」ボタンや「閉じる」ボタンのため
      */
        queryAll('#order-select_processing_list .order-select_option_item').forEach((item) => {
            if(item.querySelector('.icon-remove')?.getAttribute('data-option-name')!=='kdmr') return
            item.remove()
            renderSelectedProcessing()
          //価格表を更新
            showOrderLoader()
            setTimeout(reloadPrice, 400)
        })
    }
  //選択不可にする（上記のクリックで動く処理の後にずらす）
    setTimeout(() => {
        setProcessingDisabledBy('kdmr', KADOMARU_DISABLED_REASON, true)
    }, 0)
}

/**
 * 折りタイプの選択（パネルの選択表示の付け替えと、カート用要素への値の格納）
 *   MEMO: 旧版は、横型から横型へ変更したとき（例: 三つ折り巻き横 → 三つ折り Z 横）に角丸の向きが縦向きになっていたため、
 *         ESM 版は選択した折りの向きに常に合わせる
 *         価格表のリセットは旧版と同じく、折りの向きで価格が変わる加工が選択されている場合のみ
 */
const initOriTypeSelect = (): void => {
    queryAll<HTMLInputElement>('[name="ori_type_item"]').forEach((radio) => {
        radio.addEventListener('change', () => {
            const selectValue = document.querySelector<HTMLInputElement>('[name="ori_type_item"]:checked')?.value ?? ''
            const isTate = /TATE/.test(selectValue)
            selectKadomaruOrientation(isTate)
          //縦型の場合は、横型の折りによる選択不可を外す（ラミネート加工の選択などで選択不可になっていれば、選択不可のまま）
            if(isTate) {
                setProcessingDisabledBy('kdmr', KADOMARU_DISABLED_REASON, false)
            }
            else if(KADOMARU_DISABLE_PAGES.includes(pageId)) {
                disableKadomaru()
            }
          // .is-select の移動
            queryAll('.order-slct_panel_item').forEach((item) => item.classList.remove('is-select'))
            radio.closest('.order-slct_panel_item')?.classList.add('is-select')
            const oriTypeInput = getOriTypeInput()
            if(oriTypeInput) oriTypeInput.value = selectValue
            const hasOriDependent = ORI_DEPENDENT_PROCESSING.some((selector) => document.querySelector<HTMLInputElement>(selector)?.checked)
            if(hasOriDependent) refreshPriceTable()
        })
    })
}

const loadPriceTable = createPriceTableLoader({
    printTypes: PRINT_TYPES,
    maxLotAmount: 50,
    unitNum: 100,
    lotName: '箱',
    getLayout: () => ({
        pc: PRINT_TYPES,
        mobile: [['#order-price_table_mobile-01 tbody', PRINT_TYPES]],
    }),
})

onReady(() => {
  //折りタイプの初期値を設定し、角丸箇所指定を縦向きにする
    const oriTypeInput = getOriTypeInput()
    if(oriTypeInput && INITIAL_ORI_TYPES[pageId]) oriTypeInput.value = INITIAL_ORI_TYPES[pageId]
    selectKadomaruOrientation(true)
    setPriceLoader(loadPriceTable)
    loadPriceTable()
    initOriTypeSelect()
    initPriceLinkAddCart()
})
